import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Protocol, ProtocolType } from './dto/Protocol';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { BBResponseContainer } from '../common-services/response-container';
import { SampleFileRequest, WorkFile, WorkFileConfig, WorkFileRequest } from '../workfile-configuration/model/workfile';
import { InboundFileConfig } from '../inbound-file-configuration/model/inboundfile';
import {InboundFileResponse} from "../dto/inbound-file-response";

@Injectable({
	providedIn: 'root'
})
export class ConfigurationService {

	readonly MDM_API = environment.BB_MDMSERVICE_URL;
	readonly PLATE_PREPARATION_API = environment.BB_SAMPLE_PROCESSING_API;
	readonly SAMPLES_API = environment.BB_MA_DELIVERY_API;
	readonly GENERIC_MDM = 'mdm/generic';
	readonly CONTENT_DISPOSTION = 'Content-Disposition';

	constructor(private httpClient: HttpClient) { }

	saveProtocol(protocol: Protocol): Observable<BBResponseContainer> {
		let url = this.MDM_API;
		if(protocol.protocolType === ProtocolType.EXTRACTION){
			url += "extractionProtocol";
		}
		else{
			url += "protocol";
		}

		return this.httpClient.post<BBResponseContainer>(url, protocol).pipe(timeout(environment.defaultTimeout));
	}

	getProtocol(labId: string, protocolName: string, protocolVersion: number): Observable<BBResponseContainer> {
		let url = this.MDM_API + "protocol?labId=" + labId + "&protocolName=" + protocolName + "&protocolVersion=" + protocolVersion;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
	}

	getProtocols(labId: string,protocolTypes:string = ""): Observable<BBResponseContainer> {
		let url = this.MDM_API + "protocols/" + labId;
		if(protocolTypes.length !== 0){
			url += `?types=${protocolTypes}`;
		}
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
	}

	exportProtocol(labId: string, protocolName: string, protocolVersion: number): Observable<Object> {
		let url = this.MDM_API + "protocol/export";
		let data = {
			"labId": labId,
			"protocolName": protocolName,
			"protocolVersion": protocolVersion
		};
		return this.httpClient.put(url, data, { responseType: 'blob', observe: 'response' })
			.pipe(
				map((res: HttpResponse<Blob>) => {
					return {
						fileContent: new Blob([res.body || new Blob()], { type: res.headers.get('content-Type') || "" }),
						filename: res.headers.get(this.CONTENT_DISPOSTION)?.split("=")[1]
					}
				})
			);
	}

	importProtocol(formData: FormData): Observable<BBResponseContainer> {
		let url = this.MDM_API + "protocol/import";
		return this.httpClient.post<BBResponseContainer>(url, formData).pipe(timeout(environment.defaultTimeout));
	}

	saveWorkFileConfig(workFileConfig:WorkFileConfig): Observable<WorkFileConfig>{
		const url = `${this.MDM_API}workfile`;
		return this.httpClient.post<BBResponseContainer>(url,workFileConfig).pipe(timeout(environment.defaultTimeout),map(result => result.data as WorkFileConfig));
	}

	fetchWorkFileConfigs(labID:string,workFileDefinitionNames:string[] = []):Observable<Array<WorkFileConfig>> {
		let url = `${this.MDM_API}workfile/${labID}`;
		if(workFileDefinitionNames.length !== 0){
			url+= `?workFileDefinitionNames=${workFileDefinitionNames.join(",")}`;
		}
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout),map(result => result.data as WorkFileConfig[]));
	}

	fetchWorkFileByDefinitionName(labID:string,workfileDefinitionName:string):Observable<WorkFileConfig>{
		const url = `${this.MDM_API}workfile/${labID}/${workfileDefinitionName}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout),map(result => result.data as WorkFileConfig));
	}

    downloadWorkFileSample(workFileRequest: WorkFileRequest): Observable<WorkFile[]> {
        const url = `${this.PLATE_PREPARATION_API}workfile/generate`;
        return this.httpClient.put<{filename:string,filetype:string,contents:string}[]>(url, workFileRequest).pipe(
            map((files) => {
                const workFiles:WorkFile[] = [];
                for (const file of files) {
                    const byteCharacters = atob(file.contents);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const workfile = new WorkFile();
                    workfile.filename = file.filename;
                    workfile.fileContent = new Blob([byteArray], {type: file.filetype});
                    workFiles.push(workfile);
                }
                return workFiles;
            })
        );
	}

	downloadSampleFile(sampleFileRequest:SampleFileRequest): Observable<WorkFile> {
		const url = `${this.SAMPLES_API}samplefile/generate`;
		return this.httpClient.put(url, sampleFileRequest, { responseType: 'blob', observe: 'response' }).pipe(
			map((result:HttpResponse<Blob>) => {
				const contentDispositionHeader = result.headers.get(this.CONTENT_DISPOSTION);
				const contentType = result.headers.get("Content-Type");
				const filename = contentDispositionHeader.split("=")[1];
				const workFile = new WorkFile();
				workFile.fileContent = new Blob([result.body],{type:contentType});
				workFile.filename = filename;
				return workFile;
			})
		);
	}

	validateProtocol(data:any) {
		const url = `${this.MDM_API}validate/protocol`;
		return this.httpClient.put<BBResponseContainer>(url, data).pipe(timeout(environment.defaultTimeout),map(result => result.data));
	}

	createNewInboundFileConfig(inboundFileConfig: InboundFileConfig): Observable<InboundFileConfig> {
		const url = `${this.MDM_API}inboundFile`;
		return this.httpClient.post<BBResponseContainer>(url, inboundFileConfig).pipe(timeout(environment.defaultTimeout), map(result => result.data as InboundFileConfig));
	}

	updateInboundFileConfig(inboundFileConfig: InboundFileConfig): Observable<InboundFileConfig> {
		const url = `${this.MDM_API}inboundFile`;
		return this.httpClient.put<BBResponseContainer>(url, inboundFileConfig).pipe(timeout(environment.defaultTimeout), map(result => result.data as InboundFileConfig));
	}

	fetchInboundFileConfigs(labID: string, inboundFileDefinitionNames: string[] = []): Observable<Array<InboundFileConfig>> {
		let url = `${this.MDM_API}inboundFile/${labID}`;
		if (inboundFileDefinitionNames.length !== 0) {
			url += `?inboundFileDefinitionNames=${inboundFileDefinitionNames.join(",")}`;
		}
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout), map(result => result.data as InboundFileConfig[]));
	}

	fetchInboundFileByDefinitionName(labID: string, inboundfileDefinitionName: string): Observable<InboundFileConfig> {
		const url = `${this.MDM_API}inboundFile/${labID}/${inboundfileDefinitionName}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout), map(result => result.data as InboundFileConfig));
	}

	uploadInboundFile(inboundFileUploadRequest: FormData): Observable<InboundFileResponse> {
		const url = `${this.PLATE_PREPARATION_API}inboundFile/upload`;
		return this.httpClient.put<BBResponseContainer>(url, inboundFileUploadRequest).pipe(map(result => result.data as InboundFileResponse),timeout(environment.defaultTimeout));
	}

	createPlateBarcode(labId: string, projectName:string, year: string, barcodeFormat: string): Observable<BBResponseContainer> {
		const url = `${this.PLATE_PREPARATION_API}generic/createBarcode`;
		const requestParam = new HttpParams().set('labId', labId).set('projectName',projectName)
							.set("year",year).set("barcodeFormat",barcodeFormat);
		const options = url ? { params: requestParam } : {};
		return this.httpClient.get<BBResponseContainer>(url,options).pipe(timeout(environment.defaultTimeout));
	}
}
